import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Eye, EyeOff } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SocialLoginButtons from '../components/SocialLoginButtons';
import { trackPixelEvent } from '../hooks/useMetaPixel';

const LoginPage = () => {
  const { t } = useTranslation('auth');
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!identifier || !password) return;
    setLoading(true);
    try {
      const loggedUser = await login(identifier, password);
      // Phase 37E — client-only Login event (no server-side dedup, so no
      // event_id). Meta treats standard `Login` as a real funnel signal.
      trackPixelEvent('Login', {
        content_name: loggedUser?.is_admin ? 'admin_login' : 'user_login',
      });
      const from = location.state?.from?.pathname;
      // Admin users go straight to the admin panel
      const target = loggedUser?.is_admin ? '/admin' : (from || '/');
      navigate(target, { replace: true });
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      // Phase 27.5 — Backend signals unverified email with
      // "email_verification_required|<email>". Redirect to /verify-email.
      if (typeof detail === 'string' && detail.startsWith('email_verification_required')) {
        const emailPart = detail.split('|')[1] || '';
        toast({
          title: t('login.email_verification_toast_title'),
          description: t('login.email_verification_toast_desc'),
        });
        navigate(`/verify-email?email=${encodeURIComponent(emailPart)}`, { replace: true });
        return;
      }
      toast({
        title: t('login.error_title'),
        description: detail || t('login.error_default'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const canSubmit = identifier.length > 0 && password.length > 0 && !loading;

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4 py-10">
      <div className="mb-4 flex w-full max-w-sm justify-end">
        <LanguageSwitcher variant="full" align="end" testId="login-language-switcher" />
      </div>
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center border border-border bg-card px-10 py-10">
          <h1 className="mb-8 mt-2 text-5xl nymia-logo">
            {t('login.brand')}
          </h1>
          <form onSubmit={handleSubmit} className="flex w-full flex-col gap-2" data-testid="login-form">
            <Input
              type="text"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              placeholder={t('login.identifier_placeholder')}
              aria-label={t('login.identifier_label')}
              className="h-9 rounded-sm border-border bg-background text-xs"
              autoComplete="username"
              data-testid="login-identifier-input"
            />
            <div className="relative">
              <Input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={t('login.password_placeholder')}
                aria-label={t('login.password_label')}
                className="h-9 rounded-sm border-border bg-background pr-14 text-xs"
                autoComplete="current-password"
                data-testid="login-password-input"
              />
              {password && (
                <button
                  type="button"
                  onClick={() => setShowPwd((p) => !p)}
                  aria-label={showPwd ? t('login.hide_password') : t('login.show_password')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-foreground"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              )}
            </div>
            <Button
              type="submit"
              disabled={!canSubmit}
              className="mt-2 h-8 rounded-md gold-gradient text-sm font-semibold disabled:opacity-60"
              data-testid="login-submit-button"
            >
              {loading ? t('login.submitting') : t('login.submit')}
            </Button>
          </form>

          <SocialLoginButtons />

          <div className="my-5 flex w-full items-center gap-4">
            <div className="h-px flex-1 bg-neutral-300" />
            <span className="text-xs font-semibold text-muted-foreground">{t('login.or')}</span>
            <div className="h-px flex-1 bg-neutral-300" />
          </div>

          <Link
            to="/forgot-password"
            className="text-xs font-semibold gold-text hover:opacity-70"
            data-testid="login-forgot-password-link"
          >
            {t('login.forgot_password')}
          </Link>

          <Link
            to="/verify-email"
            className="mt-2 text-center text-[11px] font-medium text-muted-foreground hover:underline"
            data-testid="login-resend-verify-link"
          >
            {t('login.verify_hint')}
          </Link>
        </div>

        <div className="mt-3 flex items-center justify-center border border-border bg-card py-6 text-sm">
          <span className="text-foreground">{t('login.no_account')}</span>
          <Link to="/register" className="ml-1 font-semibold gold-text hover:underline">
            {t('login.signup_link')}
          </Link>
        </div>

        <p className="mt-6 text-center text-xs text-muted-foreground" data-testid="login-brand-footer">Bull Marketing S.r.l.s. · Cento (FE), Italia</p>
      </div>

      <LegalFooter className="mt-8 max-w-md px-4" />
    </div>
  );
};

export default LoginPage;
